import { Injectable }           from '@angular/core';
import { Response }             from '@angular/http';
import { Observable }           from 'rxjs/Observable';
import { BehaviorSubject }      from 'rxjs/BehaviorSubject';

import { Program }              from './../../models/program/program';
import { ProgramSingle }        from './../../models/program/program-single';
import { Session }              from './../../models/program/session';
import { SessionDetails }       from './../../models/program/session-details';
import { ProgramWithSessions }  from './../../models/program/program-with-sessions';
import { TeamNameList }         from './../../models/program/team-name-list';
import { SessionUser }          from './../../models/program/session-user';

import { ApiService }           from '../../root-services/api.service';
import { EditionListService }   from '../../editions/edition.service';

@Injectable()

export class ProgramService {
  private programsUrl = '/programs';
  private programsWithSessionsUrl = '/programs_with_sessions';
  private sessionsUrl = '/sessions';

  private _programId: number = null;
  private _editionId: number = null;

  private _programSingle: Observable<ProgramSingle> = null;
  private _programs: BehaviorSubject<Program[]> = new BehaviorSubject<Program[]>([]);
  private _programsWithSessions: BehaviorSubject<ProgramWithSessions[]> = new BehaviorSubject<ProgramWithSessions[]>([]);
  private _programWithSessions: BehaviorSubject<ProgramWithSessions> = new BehaviorSubject<ProgramWithSessions>(null);
  private _sessions: BehaviorSubject<Session[]> = new BehaviorSubject<Session[]>([]);
  private _session: BehaviorSubject<SessionDetails> = new BehaviorSubject<SessionDetails>(null);
  private _teamNameLists: BehaviorSubject<TeamNameList[]> = new BehaviorSubject<TeamNameList[]>([]);
  private _sessionUsers: BehaviorSubject<SessionUser[]> = new BehaviorSubject<SessionUser[]>(null);

  constructor(
    private api: ApiService,
    private editionList: EditionListService
  ) {
    this.editionList.waitForEdition().subscribe(edition => {
      this._editionId = edition.id;
      this.getPrograms(true);
    });
  }

  public getPrograms(reset?: boolean): BehaviorSubject<Program[]> {
    if (reset) {
      this._programs.next([]);
      let url = this.programsUrl;
      if (this._editionId) {
        url += '?edition_id=' + this._editionId;
      }
      let observable = this.api.get(url)
          .map((response: Response) => response.json() as Program[])
          .publishReplay(1).refCount();
      observable.catch(this.handleError);
      observable.subscribe(programs => this._programs.next(programs));
    }

    return this._programs;
  }

  public getProgram(id: number): Observable<ProgramSingle> {
    if (this._programId !== id) {
      this._programId = id;

      this._programSingle = this.api.get(`${this.programsUrl}/${id}`)
                                    .map((response: Response) => response.json() as ProgramSingle)
                                    .publishReplay(1).refCount()
                                    .catch(this.handleError);
    }

    return this._programSingle;
  }

  public getProgramsWithSessions(reset?: boolean): BehaviorSubject<ProgramWithSessions[]> {
    if (reset) {
      this._programsWithSessions.next([]);
      let url = this.programsWithSessionsUrl;
      let observable = this.api.get(url)
          .map((response: Response) => response.json() as ProgramWithSessions[])
          .publishReplay(1).refCount();
      observable.catch(this.handleError);
      observable.subscribe(programs => this._programsWithSessions.next(programs));
    }
    return this._programsWithSessions;
  }

  public getProgramWithSessions(programId: number, reset?: boolean): BehaviorSubject<ProgramWithSessions> {
    if (reset) {
      this._programWithSessions.next(null);
      let url = `${this.programsWithSessionsUrl}/${programId}`;
      let observable = this.api.get(url)
          .map((response: Response) => response.json() as ProgramWithSessions)
          .publishReplay(1).refCount();
      observable.catch(this.handleError);
      observable.subscribe(program => this._programWithSessions.next(program));
    }
    return this._programWithSessions;
  }

  public getSessionsSchedule(reset?: boolean): BehaviorSubject<Session[]> {
    if (reset) {
      this._sessions.next([]);
      let url = this.sessionsUrl;
      let observable = this.api.get(url)
          .map((response: Response) => response.json() as Session[])
          .publishReplay(1).refCount();
      observable.catch(this.handleError);
      observable.subscribe(sessions => this._sessions.next(sessions));
    }
    return this._sessions;
  }

  public getSession(programId: number, sessionId: number, reset?: boolean): BehaviorSubject<SessionDetails> {
    if (reset) {
      this._session.next(null);
      let url = `${this.programsUrl}/${programId}${this.sessionsUrl}/${sessionId}`;
      let observable = this.api.get(url)
          .map((response: Response) => response.json() as SessionDetails)
          .publishReplay(1).refCount();
      observable.catch(this.handleError);
      observable.subscribe(session => this._session.next(session));
    }
    return this._session;
  }

  public getTeamNameLists(): BehaviorSubject<TeamNameList[]> {
    this._teamNameLists.next(null);
    let url = `${this.programsUrl}/team_name_lists`;
    if (this._editionId) {
      url += '?edition_id=' + this._editionId;
    }
    let observable = this.api.get(url)
        .map((response: Response) => response.json() as TeamNameList[])
        .publishReplay(1).refCount();
    observable.catch(this.handleError);
    observable.subscribe(teamNameLists => this._teamNameLists.next(teamNameLists));

    return this._teamNameLists;
  }

  private clearProgram() {
    this._programSingle = null;
    this._programId = null;
  }

  public submitNewSession(session: SessionDetails, programId: number): Observable<Response> {
    let url = `${this.programsUrl}/${programId}${this.sessionsUrl}/new`;
    return this.api.post(url, session)
      .map((response: Response) => response as Response)
      .publishReplay(1).refCount()
      .catch(this.handleError);;
  }

  public updateSession(session: SessionDetails, programId: number, sessionId: number): Observable<Response> {
    let url = `${this.programsUrl}/${programId}${this.sessionsUrl}/${sessionId}`;
    return this.api.put(url, session)
      .map((response: Response) => response as Response)
      .publishReplay(1).refCount()
      .catch(this.handleError);;
  }

  public editProgram(id: number, program: Partial<ProgramSingle>): Observable<Response> {
    let body = JSON.stringify(program);

    const multicastedObservable = this.api.patch(`${this.programsUrl}/${id}`, body)
      .publish().refCount()
      .catch(this.handleError);
    multicastedObservable.subscribe(res => this.clearProgram());
    return multicastedObservable;
  }

  public getSessionUsers(programId: number, sessionId: number):  BehaviorSubject<SessionUser[]> {
    let url = `${this.programsUrl}/${programId}${this.sessionsUrl}/${sessionId}/users`;
    let observable = this.api.get(url)
      .map((response: Response) => response.json() as SessionUser[])
      .publishReplay(1).refCount()

    observable.catch(this.handleError);
    observable.subscribe(users => this._sessionUsers.next(users));
    return this._sessionUsers;
  }

  public addUsersToSession(programId: number, sessionId: number, users: {id: number}[]):  Observable<Response> {
    const url = `/programs/${programId}/sessions/${sessionId}/users`;
    const data = users.map(user => user.id);
    return this.api.post(url, data)
      .publishReplay(1).refCount()
      .catch(this.handleError);
  }

  private handleError(error: any): Promise<any> {
    console.error('An error occurred', error);
    return Promise.reject(error.message || error);
  }
}



// WEBPACK FOOTER //
// /home/runner/work/nomadic-admin/nomadic-admin/nomadic-admin/src/app/shared/services/program.service.ts